import React, { useEffect, useState } from 'react';

// project-imports
import useCommonStyles from 'helpers/commonStyles';
import { getFormattedDate, getLocalDateTimeFields } from 'helpers/dates';
import { capitalizeString } from 'helpers/stringFunctions';
import { dispatch, useSelector } from 'store';
import { rowsPerPagePaginationAlt } from 'store/constant';
import { filterEscalationsData, getEscalations } from 'store/slices/memberPriority';
import MainCard from 'ui-component/cards/MainCard';
import EnhancedTableHead from 'views/forms/tables/TableEnhanced';
import { DEFAULT_ORDER, escalationsHeadCells } from '../constant';
import useStyles from '../styles';

// material ui
import { CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';

const Escalations = () => {
    const commonClasses = useCommonStyles();
    const classes = useStyles();
    const theme = useTheme();

    const [order, setOrder] = useState(DEFAULT_ORDER);
    const [orderBy, setOrderBy] = useState('');

    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const { escalationsData = {}, isEscalationsLoading = false, selectedMember = {}, escalationFilters = {} } = memberPriorityData || {};
    const {
        count = 0,
        next = null,
        previous = null,
        results = {},
        page_size: rowsPerPage = rowsPerPagePaginationAlt,
        currentPage = 0
    } = escalationsData || {};

    const { open_close_system: escalations = [] } = results || {};

    const { code: memberCode = '' } = selectedMember || {};

    useEffect(() => {
        if (!memberCode) return;
        const filters = { search: [{ id: memberCode }], request_type: [{ id: 5 }] };
        dispatch(filterEscalationsData(filters));
        dispatch(getEscalations('', filters));
    }, [memberCode]);

    const handleChangePage = (event, nextPage) => {
        if (nextPage - currentPage > 0) {
            // next
            dispatch(getEscalations(next, '', nextPage));
        } else if (nextPage - currentPage < 0) {
            // previous
            dispatch(getEscalations(previous, '', nextPage));
        }
    };

    const handleRequestSort = React.useCallback(
        async (event, newOrderBy) => {
            const isAsc = orderBy === newOrderBy && order === 'asc';
            const toggledOrder = isAsc ? 'desc' : 'asc';
            setOrder(toggledOrder);
            setOrderBy(newOrderBy);

            const id = toggledOrder === 'desc' ? `-${newOrderBy}` : newOrderBy;

            const tempFilterTypeObj = { ordering: { id } };
            dispatch(getEscalations('', { ...escalationFilters, ordering: tempFilterTypeObj }));
        },
        [order, orderBy, rowsPerPage, escalationFilters]
    );

    return (
        <Grid item xs={12}>
            <MainCard content={false}>
                <TableContainer>
                    <Table sx={{ minWidth: 350 }} aria-label="simple table">
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={escalationsHeadCells}
                            tableCellClass={classes.tableCell}
                            tableRowClass={commonClasses.primaryBackground}
                        />
                        <TableBody>
                            {isEscalationsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                escalations.map((item) => {
                                    const {
                                        request_type_code: requestCode = '',
                                        request_type_key: requestTypeKey = '',
                                        status = '',
                                        category = {},
                                        conversation = {},
                                        code = '',
                                        request_type: requestType = {},
                                        updated_at: updatedAt = ''
                                    } = item || {};

                                    const { name: requestName = '' } = requestType || {};
                                    const { name: categoryName = '' } = category || {};
                                    const { created_by: createdBy = '', given_by: givenBy = '' } = conversation || {};

                                    const { hours24: updatedAtHrs = '', minutes: updatedAtMin = '' } =
                                        getLocalDateTimeFields(updatedAt) || {};

                                    return (
                                        <TableRow hover key="followUpTable">
                                            <TableCell sx={{ minWidth: '110px', color: theme.palette.primary.main }}>
                                                {code || '-'}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: '110px',
                                                    color: status === 'open' ? theme.palette.warning.dark : theme.palette.grey.primary
                                                }}
                                            >
                                                {capitalizeString(status, ' ')}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: '130px' }}>
                                                {requestCode ? (
                                                    <>
                                                        <Typography>{requestCode}</Typography>
                                                        <Typography variant="subtitle2" sx={{ color: theme.palette.grey[500], mt: 0.3 }}>
                                                            {capitalizeString(requestTypeKey, '_')}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ minWidth: '130px' }}>{categoryName || '-'}</TableCell>
                                            <TableCell sx={{ minWidth: '130px' }}>{requestName || '-'}</TableCell>
                                            <TableCell sx={{ minWidth: '130px' }}>{givenBy || createdBy || '-'}</TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: '120px'
                                                }}
                                                component="th"
                                            >
                                                {updatedAt ? (
                                                    <>
                                                        <Typography>{`${getFormattedDate(updatedAt)}`}</Typography>
                                                        <Typography variant="h6" sx={{ color: theme.palette.grey[500], fontWeight: 400 }}>
                                                            {`${updatedAtHrs}:${updatedAtMin}`}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[rowsPerPage]}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    page={currentPage}
                    count={parseInt(count, 10)}
                    backIconButtonProps={{
                        'aria-label': 'previous page'
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'next page'
                    }}
                    onPageChange={handleChangePage}
                    labelDisplayedRows={({ count }) => `Page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`}
                    sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                />
            </MainCard>
        </Grid>
    );
};

export default Escalations;
